import { CircleFragment, Visibility_Enum } from '@gql'
import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import useWritableCircles from '@/circle/hooks/useWritableCircles'
import { CirclePerimeter } from '../circlePerimeter'
import { filterCirclesOnly } from '../circlesOnlyFilter'
import useCirclePerimeterFilter from '../hooks/useCirclePerimeterFilter'
import useCirclePicker from '../hooks/useCirclePicker'
import { useCircleSearchItems } from '../hooks/useCircleSearchItems'
import SearchInput, { SearchInputProps } from './SearchInput'

interface Props extends Omit<SearchInputProps, 'items' | 'perimeter'> {
  circles?: CircleFragment[] // If not provided, use store
  excludeIds?: string[]
  singleMember?: boolean
  // ORG-02: the filter the list opens on. The user can switch it from the bar
  // at the top of the list; in a meeting, the list opens on the meeting's
  // circle instead (#123).
  defaultPerimeter?: CirclePerimeter
  // ORG-06: restrict the picker to circles only (roles hidden). The currently
  // selected value is never hidden, even if it's a leaf role.
  circlesOnly?: boolean
  // Hide the filter bar (REU-02, 11/08/2026).
  // Opt-in, so every existing picker keeps it. It is meant for the pickers
  // already fed a SHORT, explicit list of circles: there, the filters
  // partition a handful of items nobody needs partitioned, and cost a second
  // click to change a role — which is the friction the quick form removes.
  hidePerimeter?: boolean
  // #121: in a creation form, roles one may not write to stay listed but
  // greyed and not selectable — the server refuses them. The current value is
  // never greyed, so an object carried elsewhere can still be edited.
  disableOutsidePerimeter?: boolean
  // The visibility chosen in that form: in Role visibility, only the member's
  // own roles stay selectable, as the server requires.
  writableVisibility?: Visibility_Enum
}

export default function CircleSearchInput({
  circles,
  excludeIds,
  singleMember,
  defaultPerimeter = 'all',
  circlesOnly,
  hidePerimeter,
  disableOutsidePerimeter,
  writableVisibility = Visibility_Enum.Circle,
  ...props
}: Props) {
  const { t } = useTranslation()
  const rawItems = useCircleSearchItems(circles, excludeIds, singleMember)
  const writableCircles = useWritableCircles(undefined, writableVisibility)
  const allItems = useMemo(() => {
    if (!disableOutsidePerimeter) return rawItems
    const writableIds = new Set(writableCircles.map((circle) => circle.id))
    const reason = t(
      writableVisibility === Visibility_Enum.Role
        ? 'CirclePerimeter.notOwnRole'
        : 'CirclePerimeter.outsidePerimeter'
    )
    return rawItems.map((item) =>
      writableIds.has(item.id) || item.id === props.value
        ? item
        : { ...item, disabledReason: reason }
    )
  }, [
    rawItems,
    disableOutsidePerimeter,
    writableCircles,
    writableVisibility,
    props.value,
    t,
  ])
  // ORG-06: reset for free each time the modal remounts this component.
  const [showRoles, setShowRoles] = useState(false)
  const filterActive = !!circlesOnly && !showRoles
  const items = useMemo(
    () => (filterActive ? filterCirclesOnly(allItems, props.value) : allItems),
    [allItems, filterActive, props.value]
  )
  const handleShowRoles = () => setShowRoles(true)
  const { pickerModal, additionalTopItems, handleChange } = useCirclePicker(
    props.onChange
  )
  // Called unconditionally even when hidePerimeter is set: a hook cannot be
  // skipped, only its rendering is. Its output then goes unused, which
  // costs a comparison over an already short list — not worth a second code
  // path (review of 11/08).
  const perimeter = useCirclePerimeterFilter(defaultPerimeter)

  return (
    <>
      <SearchInput
        {...props}
        items={items}
        onChange={handleChange}
        additionalTopItems={additionalTopItems}
        bottomAction={
          filterActive
            ? {
                label: t('CircleSearchInput.showRolesToo'),
                onClick: handleShowRoles,
              }
            : undefined
        }
        perimeter={hidePerimeter ? undefined : perimeter}
      />
      {pickerModal}
    </>
  )
}
